'use client';

import { useMemo, useState } from 'react';
import { ServicePageTemplate } from '../ServicePageTemplate';

/* --- RE-STYLED INTERACTIVE GRAPHIC --- */
const HEAT_ZONE_DELTAS = [
  { label: 'Standard Deck', delta: 0, description: 'Basic OSB absorbs heat.' },
  { label: 'Radiant Barrier', delta: 17, description: 'Foil reflects 97% of UV.' },
  { label: 'Active Venting', delta: 25, description: 'Ridge vents flush hot air.' },
];

function RoofingGraphic() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [baseTemp, setBaseTemp] = useState(140); // Started slightly higher for dramatic effect
  
  const zones = useMemo(
    () =>
      HEAT_ZONE_DELTAS.map((zone) => ({
        ...zone,
        value: Math.max(baseTemp - zone.delta, 80),
      })),
    [baseTemp]
  );
  
  const active = zones[activeIndex];
  
  // Dynamic gradient based on temp
  const getGradient = (temp: number) => {
    if (temp > 130) return 'from-red-600 via-orange-500 to-amber-400';
    if (temp > 115) return 'from-orange-500 via-amber-400 to-yellow-300';
    return 'from-emerald-500 via-teal-400 to-cyan-300'; // "Cool" colors
  };

  const gradient = getGradient(active.value);

  return (
    <div className="rounded-[32px] overflow-hidden shadow-2xl">
      {/* Studio Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 bg-slate-900 px-4 py-3 sm:px-6 sm:py-4">
         <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-400 sm:text-xs sm:tracking-[0.2em]">
           Thermal Defense Lab
         </div>
         <div className="flex items-center gap-2">
           <div className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
           <span className="text-[10px] font-bold text-white/50">LIVE SENSOR</span>
         </div>
      </div>

      {/* Main Dashboard Area */}
      <div className="bg-slate-900 p-4 text-white sm:p-8">
        
        {/* Buttons Grid */}
        <div className="grid gap-3 sm:grid-cols-3">
          {zones.map((zone, idx) => {
            const isActive = idx === activeIndex;
            return (
              <button
                key={zone.label}
                type="button"
                onClick={() => setActiveIndex(idx)}
                className={`group relative overflow-hidden rounded-2xl border px-4 py-4 text-left transition-all duration-300 ${
                  isActive 
                    ? 'border-emerald-500 bg-white/10 shadow-[0_0_20px_rgba(16,185,129,0.2)]' 
                    : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20'
                }`}
              >
                <p className={`text-[10px] font-bold uppercase tracking-wider transition-colors ${isActive ? 'text-emerald-400' : 'text-slate-400'}`}>
                  {zone.label}
                </p>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-3xl font-bold tracking-tight">{zone.value}°</span>
                  <span className="text-sm text-white/50">F</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Visualizer Bar */}
        <div className="mt-8">
            <div className="mb-2 flex flex-col gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:text-xs sm:tracking-widest">
               <span>Attic Temperature</span>
               <span>{active.description}</span>
            </div>
            <div className={`min-h-[5rem] w-full rounded-2xl bg-gradient-to-r ${gradient} p-1 shadow-inner transition-all duration-700`}>
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-xl bg-slate-950/80 px-4 py-3 text-center backdrop-blur-sm sm:flex-row sm:justify-between sm:px-6 sm:text-left">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">Reduction</p>
                  <p className="text-xl font-bold text-white">
                    {Math.max(baseTemp - active.value, 0)}°F Cooler
                  </p>
                </div>
                {/* Visual Indicator Icon */}
                <div className="rounded-full bg-white/10 p-2">
                   <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                   </svg>
                </div>
              </div>
            </div>
        </div>

        {/* Interactive Slider */}
        <div className="mt-8 space-y-3 rounded-2xl border border-white/5 bg-white/5 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label htmlFor="attic-temp-slider" className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
              Outside Heat Index
            </label>
            <span className="text-xs font-bold text-emerald-400">{baseTemp}°F</span>
          </div>
          <input
            id="attic-temp-slider"
            type="range"
            min={120}
            max={160}
            value={baseTemp}
            onChange={(e) => setBaseTemp(Number(e.target.value))}
            className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-slate-700 accent-emerald-500 hover:accent-emerald-400"
          />
          <p className="text-[10px] text-slate-500 text-center pt-2">
            Drag to simulate a Texas Summer afternoon.
          </p>
        </div>
      </div>
    </div>
  );
}

/* --- MAIN PAGE COMPONENT --- */

export default function RoofingPage() {
  return (
    <ServicePageTemplate
      title="High-Performance Roofing"
      heroTagline="The Cool Roof System"
      
      // EMPATHY UPDATE: Focusing on Heat (Energy Bills) and Hail (Insurance)
      description="Your roof is the first line of defense against the Texas sun. We don't just replace shingles; we install a complete thermal defense system including radiant barrier decking and active ventilation to lower your AC bills and protect your home from hail."
      
      // VISUAL POP: Emerald Green Theme
      accent="#10B981" // Emerald-500: Vibrant Green
      surface="linear-gradient(135deg, rgba(209, 250, 229, 0.9), rgba(167, 243, 208, 0.7))" // Glassy Mint
      
      // STATS: Focused on Durability & Efficiency
      stats={[
        { label: 'Impact Rating', value: 'Class 4', helper: 'Highest hail resistance' },
        { label: 'Attic Temp', value: '-30°F', helper: 'Vs. standard roof' },
        { label: 'Warranty', value: 'Lifetime', helper: 'Transferable' },
      ]}
      
      features={[
        {
          title: 'Radiant Barrier Defense',
          description:
            'We install silver-backed decking that reflects 97% of radiant heat before it enters your attic, drastically reducing the load on your air conditioner.',
        },
        {
          title: 'Solar-Ready Decking',
          description:
            'Planning for solar? We map out the rafters and reinforce the decking where the mounts will go, ensuring a leak-proof seal for decades.',
        },
        {
          title: 'Total Water Management',
          description:
            'It’s not just about the shingles. We realign gutters, upgrade flashing, and route diverters so water flows away from your foundation and softener loops.',
        },
        {
          title: 'Insurance-Grade Docs',
          description:
            'We fly a drone inspection before and after the job. You get a complete photo report to prove the quality of the install to your insurance carrier.',
        },
      ]}
      
      funElement={<RoofingGraphic />}
      
      ctaLine="Upgrade to a Cool Roof before summer hits."
      ctaServiceKey="roofing"
    />
  );
}
